import { useCallback, useMemo, ChangeEvent } from 'react';

import { Typography } from '@material-ui/core';
import { Pagination as MuiPagination, Skeleton } from '@material-ui/lab';
import clsx from 'clsx';

import { PaginationReturn } from 'generated';

import { useStyles } from './styles';

export type PaginationPosition = 'top' | 'bottom' | 'both';
export type PaginationAlignment = 'left' | 'center' | 'right';

type PaginationProps = PaginationReturn & {
  loading?: boolean;
  alignment?: PaginationAlignment;
  position?: PaginationPosition;
  description?: string;
  onPageChange(newPage: number): void;
  limit: number;
};

export function Pagination({
  currentPage,
  totalCount,
  totalPage,
  description,
  onPageChange,
  loading,
  limit,
  alignment = 'right',
  position = 'bottom',
}: PaginationProps): JSX.Element {
  const classes = useStyles({ alignment, position });

  const memoDescription = useMemo(() => {
    if (description) {
      return description;
    }
    if (currentPage && totalCount) {
      let to = currentPage * limit;
      to = to > totalCount ? totalCount : to;

      return `${(currentPage - 1) * limit + 1} até ${to}`;
    }

    return 'N/A';
  }, [currentPage, description, limit, totalCount]);

  const handlePagination = useCallback(
    (_event: ChangeEvent<unknown>, newPage: number) => {
      if (newPage !== currentPage) {
        onPageChange(newPage);
      }
    },
    [currentPage, onPageChange],
  );

  return (
    <div className={classes.container}>
      {loading ? (
        <Skeleton
          className={classes.skeleton}
          height={24}
          width={180}
          animation="wave"
        />
      ) : (
        <>
          <Typography className={classes.dataLabel}>
            {memoDescription}
          </Typography>
          <Typography
            className={clsx(classes.dataLabel, classes.secondDataLabel)}
          >
            &nbsp; de &nbsp;
          </Typography>
          <Typography className={classes.dataLabel}>
            {totalCount} linha{totalCount > 1 ? 's' : ''}
          </Typography>
        </>
      )}
      <MuiPagination
        count={totalPage}
        page={currentPage}
        shape="rounded"
        color="primary"
        onChange={handlePagination}
        disabled={loading}
        showFirstButton
        showLastButton
      />
    </div>
  );
}
